import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { ComplaintCategory, ComplaintStatus, DeterminationOutcome, Page, UtilityComplaint, UtilityKind } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const STAFF_ROLES = ['ENERGY_OFFICER', 'COMPLAINTS_DETERMINER'];
const DETERMINER_ROLES = ['COMPLAINTS_DETERMINER'];
const UTILITY_KINDS: UtilityKind[] = ['ELECTRICITY', 'WATER', 'FUEL', 'OTHER'];
const CATEGORIES: ComplaintCategory[] = ['BILLING_DISPUTE', 'SUPPLY_INTERRUPTION', 'METER_FAULT', 'CONNECTION_DELAY', 'SERVICE_QUALITY', 'SAFETY', 'OTHER'];
const STATUSES: ComplaintStatus[] = ['RECEIVED', 'ACCEPTED', 'INVESTIGATING', 'DETERMINED', 'DISMISSED', 'WITHDRAWN'];

/** Utility complaint escalation beyond PUC's internal process, through to a formal COMPLAINTS_DETERMINER ruling (M35). */
export function Complaints() {
  const { persona } = useAuth();
  const isStaff = persona ? persona.roles.some((r) => STAFF_ROLES.includes(r)) : false;
  const isDeterminer = persona?.roles.includes('COMPLAINTS_DETERMINER') ?? false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m35/complaints?${params.toString()}`;
  }, [status, appliedQ]);
  const listQ = useApiQuery<Page<UtilityComplaint>>(path);

  return (
    <div>
      <PageHeader
        title="Utility Complaints"
        subtitle="Escalation after PUC's internal process is exhausted -> accept -> investigate -> COMPLAINTS_DETERMINER ruling (M35)."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + Log complaint escalation
          </button>
        }
      />

      <form
        className="mb-4 flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setAppliedQ(q);
        }}
      >
        <input className="field-input !w-56" placeholder="Ref or description…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button type="submit" className="btn-primary">
          Search
        </button>
      </form>

      {!isStaff ? <RoleNotice roles={STAFF_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No complaints match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Utility</th>
                <th className="table-head">Category</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((c) => (
                <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(c.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{c.refNo}</td>
                  <td className="table-cell">
                    <StatusBadge status={c.utilityType} />
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={c.category} />
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={c.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <DetailModal id={selectedId} isStaff={isStaff} isDeterminer={isDeterminer} onClose={() => setSelectedId(null)} onChanged={() => listQ.reload()} />
      ) : null}
      {showCreate ? (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function DetailModal({
  id,
  isStaff,
  isDeterminer,
  onClose,
  onChanged,
}: {
  id: string;
  isStaff: boolean;
  isDeterminer: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const q = useApiQuery<UtilityComplaint>(`/api/v1/m35/complaints/${id}`);
  const c = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissReason, setDismissReason] = useState('');
  const [outcome, setOutcome] = useState<DeterminationOutcome>('UPHELD_FOR_COMPLAINANT');
  const [notes, setNotes] = useState('');
  const [remedy, setRemedy] = useState('');

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={c ? c.refNo : 'Utility complaint'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {c ? (
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={c.status} />} />
            <Detail label="Utility" value={<StatusBadge status={c.utilityType} />} />
            <Detail label="Category" value={<StatusBadge status={c.category} />} />
            <Detail label="PUC case ref" value={c.pucCaseReference} mono />
            <Detail label="Complainant" value={c.anonymous ? 'Anonymous' : `${c.complainantId} (${c.complainantType})`} mono />
          </div>
          <p className="text-slate-700">{c.description}</p>
          {c.pucResponseSummary ? <p className="text-xs text-slate-500">PUC's response: {c.pucResponseSummary}</p> : null}
          {c.determinationOutcome ? (
            <div className="rounded-lg border border-slate-200 p-3 text-sm">
              <StatusBadge status={c.determinationOutcome} /> — {c.determinationNotes}
              {c.remedyOrdered ? <p className="mt-1 text-xs text-slate-500">Remedy ordered: {c.remedyOrdered}</p> : null}
            </div>
          ) : null}
          {c.dismissedReason ? <ErrorNote message={`Dismissed: ${c.dismissedReason}`} /> : null}

          {isStaff && c.status === 'RECEIVED' ? (
            <div className="flex gap-2">
              <button className="btn-success" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m35/complaints/${id}/accept`))}>
                Accept escalation
              </button>
              <input className="field-input" placeholder="Dismiss reason" value={dismissReason} onChange={(e) => setDismissReason(e.target.value)} />
              <button className="btn-danger" disabled={busy || dismissReason.trim().length < 3} onClick={() => act(() => api.post(`/api/v1/m35/complaints/${id}/dismiss`, { reason: dismissReason }))}>
                Dismiss
              </button>
            </div>
          ) : null}

          {isStaff && c.status === 'ACCEPTED' ? (
            <div className="flex gap-2">
              <button className="btn-primary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m35/complaints/${id}/investigate`))}>
                Move to investigation
              </button>
              <input className="field-input" placeholder="Dismiss reason" value={dismissReason} onChange={(e) => setDismissReason(e.target.value)} />
              <button className="btn-danger" disabled={busy || dismissReason.trim().length < 3} onClick={() => act(() => api.post(`/api/v1/m35/complaints/${id}/dismiss`, { reason: dismissReason }))}>
                Dismiss
              </button>
            </div>
          ) : null}

          {c.status === 'INVESTIGATING' ? (
            isDeterminer ? (
              <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                <div className="text-sm font-semibold text-slate-700">Final determination</div>
                <select className="field-input" value={outcome} onChange={(e) => setOutcome(e.target.value as DeterminationOutcome)}>
                  <option value="UPHELD_FOR_COMPLAINANT">Upheld for complainant</option>
                  <option value="UPHELD_FOR_OPERATOR">Upheld for operator</option>
                  <option value="PARTIALLY_UPHELD">Partially upheld</option>
                  <option value="NO_FURTHER_ACTION">No further action</option>
                </select>
                <textarea className="field-input" rows={2} placeholder="Determination notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
                <input className="field-input" placeholder="Remedy ordered (optional)" value={remedy} onChange={(e) => setRemedy(e.target.value)} />
                <button
                  className="btn-primary"
                  disabled={busy || notes.trim().length < 3}
                  onClick={() => act(() => api.post(`/api/v1/m35/complaints/${id}/determine`, { outcome, notes, remedyOrdered: remedy || undefined }))}
                >
                  Issue determination
                </button>
              </div>
            ) : isStaff ? (
              <RoleNotice roles={DETERMINER_ROLES} />
            ) : null
          ) : null}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">Received {formatDateTime(c.createdAt)}</div>
        </div>
      ) : null}
    </Modal>
  );
}

function CreateModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [anonymous, setAnonymous] = useState(false);
  const [utilityType, setUtilityType] = useState<UtilityKind>('ELECTRICITY');
  const [category, setCategory] = useState<ComplaintCategory>('BILLING_DISPUTE');
  const [description, setDescription] = useState('');
  const [pucCaseReference, setPucCaseReference] = useState('');
  const [pucResponseSummary, setPucResponseSummary] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m35/complaints', {
        anonymous,
        utilityType,
        category,
        description,
        pucCaseReference,
        pucResponseSummary: pucResponseSummary || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to log complaint');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Log utility complaint escalation" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} />
          Anonymous complainant
        </label>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Utility type">
            <select className="field-input" value={utilityType} onChange={(e) => setUtilityType(e.target.value as UtilityKind)}>
              {UTILITY_KINDS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Category">
            <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value as ComplaintCategory)}>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="PUC case reference (required — proof the internal process was exhausted)">
          <input className="field-input" value={pucCaseReference} onChange={(e) => setPucCaseReference(e.target.value)} />
        </Field>
        <Field label="PUC's response summary (optional)">
          <input className="field-input" value={pucResponseSummary} onChange={(e) => setPucResponseSummary(e.target.value)} />
        </Field>
        <Field label="Description">
          <textarea className="field-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || description.trim().length < 10 || !pucCaseReference.trim()} onClick={submit}>
            {busy ? 'Submitting…' : 'Log escalation'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
